import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import logo from "../../assets/scenathon_logo.png";
import { useNavigate } from "react-router-dom";
import FancySelect from "../../components/FancySelect"; // 🔁 ajusta esta ruta

const ORG_OPTIONS = [
  { value: "IIASA", label: "IIASA" },
  { value: "SDSN", label: "SDSN" },
  { value: "Country Teams", label: "Country/Region Teams" },
  { value: "Other", label: "Other" },
];

export default function Step1({ onNext }) {
  const navigate = useNavigate();
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  const [formData, setFormData] = useState({
    first_name: "",
    second_name: "",
    last_name: "",
    email: "",
    organization: "",
    country: "",
    password: "",
    confirmPassword: "",
    accepted: false,
  });

  useEffect(() => {
    if (formData.organization !== "Country Teams" && formData.country) {
      setFormData((prev) => ({ ...prev, country: "" }));
    }
  }, [formData.organization, formData.country]);

  // Load countries from definedscenarios.location
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const mapToOptions = (locations) => {
      const normalized = (Array.isArray(locations) ? locations : [])
        .map((loc) => String(loc || "").trim())
        .filter(Boolean);

      const uniqueSorted = Array.from(new Set(normalized)).sort((a, b) =>
        a.localeCompare(b),
      );

      return uniqueSorted.map((country) => ({
        value: country,
        label: country,
      }));
    };

    const loadCountries = async () => {
      try {
        setLoadingCountries(true);

        const apiBase = String(process.env.REACT_APP_API_URL || "")
          .trim()
          .replace(/\/$/, "");
        const endpoints = [
          apiBase ? `${apiBase}/api/auth/locations` : null,
          "/api/auth/locations",
        ].filter(Boolean);

        let options = [];
        let loaded = false;

        for (const url of endpoints) {
          try {
            const response = await fetch(url, {
              method: "GET",
              signal: controller.signal,
            });

            if (!response.ok) continue;

            const data = await response.json().catch(() => ({}));
            const locations = Array.isArray(data?.locations) ? data.locations : [];
            const optionsFromApi = Array.isArray(data?.options)
              ? data.options
                  .map((opt) => ({
                    value: String(opt?.value || "").trim(),
                    label: String(opt?.label || opt?.value || "").trim(),
                  }))
                  .filter((opt) => opt.value)
              : [];

            options = optionsFromApi.length ? optionsFromApi : mapToOptions(locations);
            loaded = true;
            break;
          } catch (e) {
            if (e?.name === "AbortError") throw e;
          }
        }

        if (!loaded) {
          throw new Error("Could not load countries from /api/auth/locations");
        }

        if (alive) setCountryOptions(options);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Load countries error:", err);
          if (alive) setCountryOptions([]);
        }
      } finally {
        if (alive) setLoadingCountries(false);
      }
    };

    loadCountries();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.accepted) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "You must accept the Terms and Conditions.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Passwords do not match.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    if (
      formData.organization === "Country Teams" &&
      !formData.country
    ) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a Country/Region for Country Teams.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const step1Data = {
      first_name: formData.first_name,
      second_name: formData.second_name,
      last_name: formData.last_name,
      email: formData.email,
      organization: formData.organization,
      country: formData.country || null,
      password: formData.password,
    };

    onNext(step1Data);
  };

  const isCountryEnabled = formData.organization === "Country Teams";

  return (
    <div className="w-full min-h-screen bg-[#EDF0F2] flex justify-center items-center p-8">
      {/* Close button - outside form section */}
        <button
          onClick={() => navigate("/")}
          className="absolute bg-white top-6 left-6 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition z-10"
          title="Back to Login"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      <div className="bg-white rounded-2xl shadow-md w-full max-w-5xl flex overflow-hidden relative">
        

        {/* Left form section */}
        <div className="w-1/2 p-10 flex flex-col justify-start items-start">
          <h2 className="text-3xl font-medium mb-4">Create account</h2>

          <form onSubmit={handleSubmit} className="space-y-6 w-full">
            {/* First name */}
            <div className="relative w-full mt-6">
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="First Name"
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-gray-400 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none"
              >
                First Name
              </label>
            </div>

            {/* Second name (optional) */}
            <div className="relative w-full mt-6">
              <input
                type="text"
                name="second_name"
                value={formData.second_name}
                onChange={handleChange}
                placeholder="Second Name (optional)"
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-gray-400 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                pointer-events-none"
              >
                Second Name (optional)
              </label>
            </div>

            {/* Last name */}
            <div className="relative w-full mt-6">
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Last Name"
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-gray-400 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none"
              >
                Last Name
              </label>
            </div>

            {/* Email */}
            <div className="relative w-full mt-6">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-slate-600 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-600
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none"
              >
                Email
              </label>
            </div>

            {/* Organization (FancySelect) */}
            <FancySelect
              label="Organization"
              value={formData.organization}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, organization: val }))
              }
              options={ORG_OPTIONS}
              placeholder="Select an organization..."
              className="mt-6"
            />

            {/* Country (FancySelect) */}
            <FancySelect
              label="Country o Region"
              value={formData.country}
              onChange={(val) => setFormData((prev) => ({ ...prev, country: val }))}
              options={countryOptions}
              placeholder={
                isCountryEnabled
                  ? loadingCountries
                    ? "Loading countries..."
                    : countryOptions.length
                      ? "Select a Country/Region..."
                      : "No countries available"
                  : "Available only for Country/Region Teams"
              }
              disabled={!isCountryEnabled || loadingCountries}
              className="mt-6"
            />

            {/* Password */}
            <div className="relative w-full mt-6">
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-gray-400 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none"
              >
                Password
              </label>
            </div>

            {/* Confirm password */}
            <div className="relative w-full mt-6">
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              />
              <label
                className="absolute left-0 text-gray-400 text-base 
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none"
              >
                Confirm Password
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="accepted"
                checked={formData.accepted}
                onChange={handleChange}
              />
              <label>
                I agree to{" "}
                <button
                  type="button"
                  onClick={() =>
                    navigate("/terminos", { state: { from: "/registration" } })
                  }
                  className="text-green-600 font-semibold"
                >
                  Terms and Conditions
                </button>
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white py-3 rounded-xl"
            >
              Register
            </button>
          </form>
        </div>

        {/* Right image section */}
        <div className="w-1/2 bg-[#FFFFF] relative rounded-r-2xl flex items-center justify-center">
          <div
            style={{
              width: "91%",
              height: "93%",
              background: "#c1dbea",
              borderRadius: 12,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <img
              style={{
                width: 232,
                height: 228,
                objectFit: "contain",
              }}
              src={logo}
              alt="Scenathon logo"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
